import * as React from 'react'
import { clsx } from 'clsx'
import {
  PinBottomIcon,
  PinTopIcon,
  RowSpacingIcon,
} from '@radix-ui/react-icons'

import type { Block, BlockChange } from '@basehub/data/replicache/index'
import { makeInstanceChildBlockChange } from '@basehub/data/replicache/index'
import type { TreeManager } from '@basehub/utils/tree/index'

import { Button } from '~/sotano-kit/primitives/button'
import { DiffChildBlock } from './block/child'

import type { UseDiffResult } from '~/lib/diff/use-diff'
import { Switch, SwitchThumb } from '~/sotano-kit/primitives/switch'
import { SwitchProps } from '@radix-ui/react-switch'
import { getUnomdifiedBlockChange } from './block-diff'

export type DiffSeparator = {
  status: 'separator'
  blocksInside: string[]
}

export const DiffSeparator = ({
  blocks,
  treeNew,
  treeOld,
  changesTree,
  diff,
  width,
  handleToggle,
  isOpen,
  displayBlockLinks = true,
  diffTabPrefix,
  enableComments,
}: {
  blocks: string[]
  treeNew: TreeManager<Block, NonNullable<unknown>> | undefined
  treeOld: TreeManager<Block, NonNullable<unknown>> | undefined
  changesTree: TreeManager<BlockChange, NonNullable<unknown>> | undefined
  diff: UseDiffResult['diff']
  width: number | string
  handleToggle: React.MouseEventHandler<HTMLButtonElement>
  isOpen?: boolean
  displayBlockLinks?: boolean
  diffTabPrefix?: string
  enableComments?: boolean
}) => {
  return (
    <div
      className={clsx(
        'relative grid grid-cols-[minmax(0,1fr)_minmax(0,64px)_minmax(0,1fr)] py-28 gap-y-8 col-start-1 col-end-4 min-h-2 left-1/2 -translate-x-1/2 z-50',
        !isOpen && '!pb-0 !pt-0 !h-4'
      )}
    >
      <hr
        className="absolute left-1/2 -translate-x-1/2 top-0 -translate-y-1/2 z-[-1] border-b border-dark-border-solid border-t-0"
        style={{ width }}
      />
      <hr
        className="absolute left-1/2 -translate-x-1/2 bottom-0 -translate-y-1/2 z-[-1] border-b border-dark-border-solid border-t-0"
        style={{ width }}
      />

      {isOpen && (
        <SeparatorButton
          onClick={handleToggle}
          side="top"
          icon={<PinBottomIcon />}
          style={{ width }}
        >
          Hide {blocks.length} unmodified blocks
        </SeparatorButton>
      )}
      {isOpen ? (
        blocks.map((blockId, i) => {
          let change = changesTree?.getNode(blockId)

          if (!change) {
            change = getUnomdifiedBlockChange({
              changeId: blockId,
              treeNew: treeNew!,
              treeOld: treeOld!,
            })
          }

          if (!change || !change.parentKey || !diff) return null
          const parentChange = diff?.changesTree.getNode(change.parentKey)

          let child = change.value
          if (parentChange) {
            /**
             * parent is instance. i should get the "enhanced" previous title and new title.
             *
             * - if it's deleted, then there's no "new"
             * - if it's created, then there's no "previous"
             */
            child = makeInstanceChildBlockChange({
              child,
              treeNew,
              treeOld,
            })
          }

          return (
            <DiffChildBlock
              statusFromParent={
                diff?.changesTree.getNode(blockId)?.value?.status ??
                'unmodified'
              }
              commentsControls={enableComments}
              displayBlockLink={displayBlockLinks}
              key={blockId}
              child={child}
              index={i}
              changesTree={changesTree}
              treeNew={treeNew}
              treeOld={treeOld}
              diffTabPrefix={diffTabPrefix}
            />
          )
        })
      ) : (
        <SeparatorButton onClick={handleToggle} icon={<RowSpacingIcon />}>
          Show {blocks.length} unmodified blocks
        </SeparatorButton>
      )}
      {isOpen && (
        <SeparatorButton
          onClick={handleToggle}
          side="bottom"
          icon={<PinTopIcon />}
          style={{ width }}
        >
          Hide {blocks.length} unmodified blocks
        </SeparatorButton>
      )}
    </div>
  )
}

const SeparatorButton = ({
  onClick,
  icon,
  side,
  children,
}: {
  onClick: React.MouseEventHandler<HTMLButtonElement>
  icon: React.ReactNode
  side?: 'top' | 'bottom'
  children?: React.ReactNode
  style?: React.CSSProperties
}) => (
  <Button
    className={clsx('absolute z-50 left-1/2 -translate-x-1/2', {
      'top-0 -translate-y-1/2': side === 'top',
      'bottom-0 translate-y-1/2': side === 'bottom',
      '-translate-y-1/2 top-1/2': !side,
    })}
    onClick={onClick}
    size="large"
    icon={icon}
    intent="secondary"
  >
    {children}
  </Button>
)

export const DiffSeparatorSwitch: React.FC<
  React.PropsWithChildren<{
    separators: (DiffSeparator & { open: boolean })[]
    hasUnmodifiedChildren: SwitchProps['disabled']
    onCheckedChange: SwitchProps['onCheckedChange']
  }>
> = ({ hasUnmodifiedChildren, separators, onCheckedChange }) => {
  const separatorSwitchRef = React.useRef<HTMLButtonElement>(null)

  return (
    <div className="flex items-center justify-end col-span-2 text-xs leading-normal text-dark-muted">
      <label
        htmlFor="show-unchanged-switch"
        className="max-w-max text-xs leading-normal mr-2.5"
      >
        Show Unchanged
      </label>
      <Switch
        className="disabled:cursor-not-allowed"
        disabled={!hasUnmodifiedChildren}
        id="show-unchanged-switch"
        checked={
          separators.length === 0 ? false : separators?.every((s) => s.open)
        }
        ref={separatorSwitchRef}
        onCheckedChange={(checked: boolean) => {
          const label =
            separatorSwitchRef.current?.parentElement?.querySelector<HTMLSpanElement>(
              'label'
            )

          if (label) {
            label.innerText = `${checked ? 'Hide' : 'Show'} Unchanged`
          }

          onCheckedChange?.(checked)
        }}
      >
        <SwitchThumb />
      </Switch>
    </div>
  )
}
